import {
  Badge,
  Button,
  Card,
  EmptyState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@forge/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Activity, RefreshCw } from "lucide-react";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { formatDateTime } from "../../lib/format";

export const Route = createFileRoute("/_app/activity")({
  staticData: { layout: "wide" },
  component: ActivityPage,
});

const LEVEL_VARIANTS: Record<string, "neutral" | "warning" | "danger"> = {
  info: "neutral",
  warn: "warning",
  error: "danger",
};

const LEVEL_LABELS: Record<string, string> = {
  info: "Info",
  warn: "Aviso",
  error: "Erro",
};

function ActivityPage() {
  const logs = useQuery({
    queryKey: ["sync-logs"],
    queryFn: async () =>
      unwrap(await api["sync-logs"].get({ query: { limit: 100 } })),
    refetchInterval: 15_000,
  });

  return (
    <>
      <PageHeader
        title="Atividade"
        description="Trilha de auditoria de todas as sincronizações"
        actions={
          <Button
            variant="secondary"
            size="sm"
            loading={logs.isFetching}
            onClick={() => logs.refetch()}
          >
            <RefreshCw />
            Atualizar
          </Button>
        }
      />

      <Card>
        {logs.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner className="size-5 text-ink-muted" />
          </div>
        ) : logs.data && logs.data.logs.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-20">Nível</TableHead>
                <TableHead className="w-40">Evento</TableHead>
                <TableHead>Detalhes</TableHead>
                <TableHead className="w-32">Conta</TableHead>
                <TableHead className="w-32 text-right">Quando</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.data.logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Badge variant={LEVEL_VARIANTS[log.level] ?? "neutral"} dot>
                      {LEVEL_LABELS[log.level] ?? log.level}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-2xs text-ink-muted">
                    {log.kind}
                  </TableCell>
                  <TableCell className="text-ink">{log.message}</TableCell>
                  <TableCell className="text-2xs text-ink-muted">
                    {log.accountNickname ?? "—"}
                  </TableCell>
                  <TableCell className="text-right text-2xs text-ink-muted">
                    {formatDateTime(log.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            icon={<Activity />}
            title="Nenhuma atividade registrada"
            description="Cada sincronização de pedido, anúncio ou estoque aparecerá aqui."
          />
        )}
      </Card>
    </>
  );
}
